import React, {useCallback, useMemo, useState} from 'react';
import {Button, TableCell, TableRow} from '@material-ui/core';
import {FormattedMessage, useIntl} from 'react-intl';
import cn from 'classnames';
import {useAppDispatch} from '../../../../store';
import {addDeviceGroup} from '../../../../store/prodDeployments/dashboard';
import {toast} from '../../../../base-components/StudioToast';
import {StudioCheckbox} from '../../../../base-components/StudioCheckbox';
import {AddOrUpdateDeviceGroup} from '../../../../types/deployment/DeploymentDeviceGroup';
import {StudioTextField} from '../../../../base-components/StudioTextField';
import {StudioSelect} from '../../../../base-components/StudioSelect';
import {useDeviceGroupNodes} from '../useDeviceGroupNodes';
import {DeviceStatus} from '../../DeploymentStatus/DeviceStatus';

type AddNewGroupDeviceType = {
  toggleAddNewDeviceGroup: () => void;
};

const AddNewGroupDevice = ({toggleAddNewDeviceGroup}: AddNewGroupDeviceType) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [formDetails, setFormDetails] = useState<{name: string; nodes: string[]}>({
    name: '',
    nodes: [],
  });

  const [offlineFlag, setOfflineFlag] = useState(false);
  const [onlineFlag, setOnlineFlag] = useState(false);

  const {data: response} = useDeviceGroupNodes({});

  const deviceGroupNodes = useMemo(
    () => response?.flatMap(deviceGroupNode => deviceGroupNode?.body?.entries) || [],
    [response]
  );

  const data = useMemo(
    () =>
      offlineFlag && onlineFlag
        ? deviceGroupNodes
        : offlineFlag
        ? deviceGroupNodes.filter(n => n.status === 'OFFLINE')
        : onlineFlag
        ? deviceGroupNodes.filter(n => n.status === 'ONLINE')
        : deviceGroupNodes,
    [deviceGroupNodes, offlineFlag, onlineFlag]
  );

  const options = (data || []).reduce((acc: Record<string, string>, curr) => {
    acc[curr.id] = curr.name;
    return acc;
  }, {});

  const allOptions = (deviceGroupNodes || []).reduce(
    (acc: Record<string, string>, curr) => {
      acc[curr.id] = curr.name;
      return acc;
    },
    {}
  );

  const handleAdDeviceGroup = async (data: AddOrUpdateDeviceGroup) => {
    await dispatch(addDeviceGroup({request: data, nodes: deviceGroupNodes}));
    toast.success(`${data.name} has been successfully created as a device group!`);
    toggleAddNewDeviceGroup();
  };

  const renderStartAdornment = useCallback(
    (val: string) => {
      const status = deviceGroupNodes.find(d => d.id === val)?.status;
      return status && <DeviceStatus status={status} showLabel={false} />;
    },
    [deviceGroupNodes]
  );

  return (
    <TableRow className={cn('prod-deploy-dashboard__row', 'prod-deploy-dashboard__')}>
      <TableCell colSpan={1}>
        <StudioCheckbox checked={false} name="check" />
      </TableCell>
      <TableCell className="prod-deploy-dashboard__edit">
        <StudioTextField
          placeholder={intl.formatMessage({
            id: 'prodDeployment.deviceGroupPlaceholder',
          })}
          name="name"
          margin="normal"
          variant="filled"
          id="name"
          fullWidth
          multiline={false}
          data-testid="edit-device-group-name"
          useFieldProps={false}
          value={formDetails.name}
          strategy="studio"
          onChange={e => {
            setFormDetails({
              ...formDetails,
              name: e.target.value,
            });
          }}
        />
      </TableCell>
      <TableCell />
      <TableCell className="prod-deploy-dashboard__edit">
        <StudioSelect
          className="prod-deploy-dashboard__add_device_select"
          options={options}
          allOptions={allOptions}
          SelectProps={{
            value: formDetails.nodes,
            onChange: event => {
              setFormDetails({
                ...formDetails,
                nodes: event.target.value as string[],
              });
            },
            multiple: true,
          }}
          placeholder={intl.formatMessage({
            id: 'prodDeployment.deviceGroupSelectDevices',
          })}
          search
          longMenuContainer
          renderStartAdornment={renderStartAdornment}
          showDone={false}
          onChipDelete={val => {
            setFormDetails({
              ...formDetails,
              nodes: formDetails.nodes.filter(n => n !== val),
            });
          }}
          additionalMenu={
            <div
              className="prod-deploy-dashboard__status"
              data-testid="studio-select-item"
            >
              <span
                onClickCapture={e => {
                  e.stopPropagation();
                  setOnlineFlag(!onlineFlag);
                }}
              >
                <StudioCheckbox
                  checked={onlineFlag}
                  name="check"
                  label="Online"
                  onClick={e => {
                    e.stopPropagation();
                    setOnlineFlag(!onlineFlag);
                  }}
                />
              </span>
              <span
                onClickCapture={e => {
                  e.stopPropagation();
                  setOfflineFlag(!offlineFlag);
                }}
              >
                <StudioCheckbox
                  checked={offlineFlag}
                  name="check"
                  label="Offline"
                  onClick={e => {
                    e.stopPropagation();
                    setOfflineFlag(!offlineFlag);
                  }}
                />
              </span>
            </div>
          }
        />
      </TableCell>
      <TableCell />
      <TableCell colSpan={3}>
        <div className="prod-deploy-dashboard__device_group_edit">
          <Button onClick={toggleAddNewDeviceGroup} size="large" variant="outlined">
            <FormattedMessage id="form.cancel" />
          </Button>
          <Button
            onClick={() => handleAdDeviceGroup(formDetails)}
            color="primary"
            size="large"
            variant="contained"
            type="submit"
            data-testid="create-project-page-submit"
            id="create-project-page-submit"
            disabled={!formDetails.name.length}
          >
            <FormattedMessage id="prodDeployment.deviceGroupSave" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default AddNewGroupDevice;
